import React from 'react';
import {
  Box,
  Heading,
  Flex,
  Text,
  Button,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Input,
  Select,
  IconButton,
  useToast,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiPlus, FiTrash2 } from 'react-icons/fi';
import xhr from 'xhr';

export default function CobrosTarifas() {
  const navigate = useNavigate();
  const toast = useToast();

  const [loading, setLoading] = React.useState(true);
  const [programTypes, setProgramTypes] = React.useState([]);
  const [schools, setSchools] = React.useState([]);
  const [baseInputs, setBaseInputs] = React.useState({});
  const [baseRuleIds, setBaseRuleIds] = React.useState({});
  const [savingBase, setSavingBase] = React.useState({});
  const [overrides, setOverrides] = React.useState([]);
  const [overrideInputs, setOverrideInputs] = React.useState({});
  const [savingOverride, setSavingOverride] = React.useState({});
  const [newOverride, setNewOverride] = React.useState({ school: '', program_type: '', price: '' });
  const [addingOverride, setAddingOverride] = React.useState(false);

  const fetchData = React.useCallback(async () => {
    try {
      const [programTypesRes, schoolsRes, rulesRes] = await Promise.all([
        xhr('/program-types?filters[active][$eq]=true&sort[0]=order:asc&pagination[pageSize]=100'),
        xhr('/schools?sort[0]=name:asc&pagination[pageSize]=200'),
        xhr('/school-price-rules?populate=school,program_type&pagination[pageSize]=500'),
      ]);

      const types = programTypesRes.data.data || [];
      const rules = rulesRes.data.data || [];

      setProgramTypes(types);
      setSchools(schoolsRes.data.data || []);

      const baseInputsMap = {};
      const baseRuleIdsMap = {};
      const overrideRules = [];

      rules.forEach((rule) => {
        const attrs = rule.attributes || rule;
        const school = attrs.school?.data || attrs.school;
        const programType = attrs.program_type?.data || attrs.program_type;
        const programTypeId = programType?.id;

        if (!programTypeId) return;

        if (school) {
          overrideRules.push({
            id: rule.id,
            schoolId: school.id,
            schoolName: school.attributes?.name || school.name,
            programTypeId,
            programTypeName: programType.attributes?.name || programType.name,
            price: attrs.price,
          });
        } else {
          baseInputsMap[programTypeId] = String(attrs.price ?? '');
          baseRuleIdsMap[programTypeId] = rule.id;
        }
      });

      types.forEach((pt) => {
        if (!(pt.id in baseInputsMap)) {
          baseInputsMap[pt.id] = '';
        }
      });

      setBaseInputs(baseInputsMap);
      setBaseRuleIds(baseRuleIdsMap);
      setOverrides(overrideRules);

      const overrideInputsMap = {};
      overrideRules.forEach((o) => {
        overrideInputsMap[o.id] = String(o.price ?? '');
      });
      setOverrideInputs(overrideInputsMap);
    } catch (error) {
      console.error('Error fetching tarifas:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const programTypeName = (id) => {
    const pt = programTypes.find((p) => p.id === Number(id));
    return (pt?.attributes || pt)?.name || `#${id}`;
  };

  const saveBase = async (programTypeId) => {
    const value = baseInputs[programTypeId];
    if (value === '' || Number(value) < 0 || isNaN(Number(value))) {
      toast({ title: 'Tarifa inválida', description: 'Ingresa un valor mayor o igual a 0', status: 'warning', position: 'bottom', duration: 4000, isClosable: true });
      return;
    }

    try {
      setSavingBase((s) => ({ ...s, [programTypeId]: true }));
      const ruleId = baseRuleIds[programTypeId];

      if (ruleId) {
        await xhr.put(`/school-price-rules/${ruleId}`, {
          data: { program_type: programTypeId, price: Number(value) },
        });
      } else {
        const res = await xhr.post('/school-price-rules', {
          data: { program_type: programTypeId, price: Number(value) },
        });
        setBaseRuleIds((prev) => ({ ...prev, [programTypeId]: res.data.data.id }));
      }

      toast({ title: 'Tarifa guardada', status: 'success', position: 'bottom', duration: 3000, isClosable: true });
    } catch (error) {
      const message = error.response?.data?.error?.message || 'Error al guardar la tarifa';
      toast({ title: 'Error', description: message, status: 'error', position: 'bottom', duration: 5000, isClosable: true });
    } finally {
      setSavingBase((s) => ({ ...s, [programTypeId]: false }));
    }
  };

  const saveOverride = async (rule) => {
    const value = overrideInputs[rule.id];
    if (value === '' || Number(value) < 0 || isNaN(Number(value))) {
      toast({ title: 'Tarifa inválida', description: 'Ingresa un valor mayor o igual a 0', status: 'warning', position: 'bottom', duration: 4000, isClosable: true });
      return;
    }

    try {
      setSavingOverride((s) => ({ ...s, [rule.id]: true }));
      await xhr.put(`/school-price-rules/${rule.id}`, {
        data: { program_type: rule.programTypeId, price: Number(value) },
      });
      toast({ title: 'Tarifa guardada', status: 'success', position: 'bottom', duration: 3000, isClosable: true });
      setOverrides((prev) => prev.map((o) => (o.id === rule.id ? { ...o, price: Number(value) } : o)));
    } catch (error) {
      const message = error.response?.data?.error?.message || 'Error al guardar la tarifa';
      toast({ title: 'Error', description: message, status: 'error', position: 'bottom', duration: 5000, isClosable: true });
    } finally {
      setSavingOverride((s) => ({ ...s, [rule.id]: false }));
    }
  };

  const removeOverride = async (rule) => {
    try {
      await xhr.delete(`/school-price-rules/${rule.id}`);
      setOverrides((prev) => prev.filter((o) => o.id !== rule.id));
      toast({ title: 'Tarifa eliminada', status: 'success', position: 'bottom', duration: 3000, isClosable: true });
    } catch (error) {
      const message = error.response?.data?.error?.message || 'Error al eliminar la tarifa';
      toast({ title: 'Error', description: message, status: 'error', position: 'bottom', duration: 5000, isClosable: true });
    }
  };

  const addOverride = async () => {
    if (!newOverride.school || !newOverride.program_type || newOverride.price === '' || Number(newOverride.price) < 0) {
      toast({ title: 'Completa todos los campos', status: 'warning', position: 'bottom', duration: 4000, isClosable: true });
      return;
    }

    try {
      setAddingOverride(true);
      const res = await xhr.post('/school-price-rules', {
        data: {
          school: Number(newOverride.school),
          program_type: Number(newOverride.program_type),
          price: Number(newOverride.price),
        },
      });

      const school = schools.find((s) => s.id === Number(newOverride.school));

      setOverrides((prev) => [
        ...prev,
        {
          id: res.data.data.id,
          schoolId: Number(newOverride.school),
          schoolName: (school?.attributes || school)?.name,
          programTypeId: Number(newOverride.program_type),
          programTypeName: programTypeName(newOverride.program_type),
          price: Number(newOverride.price),
        },
      ]);
      setOverrideInputs((prev) => ({ ...prev, [res.data.data.id]: String(newOverride.price) }));
      setNewOverride({ school: '', program_type: '', price: '' });
      toast({ title: 'Tarifa agregada', status: 'success', position: 'bottom', duration: 3000, isClosable: true });
    } catch (error) {
      const message = error.response?.data?.error?.message || 'Error al agregar la tarifa';
      toast({ title: 'Error', description: message, status: 'error', position: 'bottom', duration: 5000, isClosable: true });
    } finally {
      setAddingOverride(false);
    }
  };

  if (loading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="lg" />
      </Box>
    );
  }

  return (
    <Box p={4}>
      <Flex alignItems="center" gap={3} mb={4}>
        <Button leftIcon={<FiArrowLeft />} variant="ghost" onClick={() => navigate('/plataforma/administrador/instituciones/cobros')}>
          Volver
        </Button>
      </Flex>

      <Heading size="md" mb={2}>
        Tarifas de activación de materia
      </Heading>
      <Text fontSize="sm" color="gray.500" mb={6}>
        Valor que se cobra a las instituciones aliadas por cada activación de materia, según el tipo de programa.
      </Text>

      <Heading size="sm" mb={3}>
        Tarifas base
      </Heading>
      <TableContainer bg="white" borderRadius="lg" boxShadow="sm" mb={8}>
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th>Programa</Th>
              <Th isNumeric>Tarifa (COP)</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {programTypes.map((pt) => {
              const attrs = pt.attributes || pt;
              return (
                <Tr key={pt.id}>
                  <Td>{attrs.name}</Td>
                  <Td isNumeric>
                    <Input
                      type="number"
                      size="sm"
                      textAlign="right"
                      maxW="160px"
                      ml="auto"
                      value={baseInputs[pt.id] ?? ''}
                      onChange={(e) => setBaseInputs((prev) => ({ ...prev, [pt.id]: e.target.value }))}
                      placeholder="0"
                    />
                  </Td>
                  <Td>
                    <IconButton
                      aria-label="Guardar"
                      icon={<FiSave />}
                      size="sm"
                      colorScheme="purple"
                      variant="ghost"
                      isLoading={!!savingBase[pt.id]}
                      onClick={() => saveBase(pt.id)}
                    />
                  </Td>
                </Tr>
              );
            })}
            {programTypes.length === 0 && (
              <Tr>
                <Td colSpan={3} textAlign="center" py={8} color="gray.500">
                  No hay tipos de programa activos
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>

      <Heading size="sm" mb={3}>
        Overrides por institución
      </Heading>
      <TableContainer bg="white" borderRadius="lg" boxShadow="sm" mb={4}>
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th>Institución</Th>
              <Th>Programa</Th>
              <Th isNumeric>Tarifa (COP)</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {overrides.map((rule) => (
              <Tr key={rule.id}>
                <Td>{rule.schoolName}</Td>
                <Td>{rule.programTypeName}</Td>
                <Td isNumeric>
                  <Input
                    type="number"
                    size="sm"
                    textAlign="right"
                    maxW="160px"
                    ml="auto"
                    value={overrideInputs[rule.id] ?? ''}
                    onChange={(e) => setOverrideInputs((prev) => ({ ...prev, [rule.id]: e.target.value }))}
                  />
                </Td>
                <Td>
                  <Flex gap={1}>
                    <IconButton
                      aria-label="Guardar"
                      icon={<FiSave />}
                      size="sm"
                      colorScheme="purple"
                      variant="ghost"
                      isLoading={!!savingOverride[rule.id]}
                      onClick={() => saveOverride(rule)}
                    />
                    <IconButton
                      aria-label="Eliminar"
                      icon={<FiTrash2 />}
                      size="sm"
                      colorScheme="red"
                      variant="ghost"
                      onClick={() => removeOverride(rule)}
                    />
                  </Flex>
                </Td>
              </Tr>
            ))}
            <Tr>
              <Td>
                <Select
                  size="sm"
                  placeholder="Institución"
                  value={newOverride.school}
                  onChange={(e) => setNewOverride((p) => ({ ...p, school: e.target.value }))}
                >
                  {schools.map((s) => (
                    <option key={s.id} value={s.id}>
                      {(s.attributes || s).name}
                    </option>
                  ))}
                </Select>
              </Td>
              <Td>
                <Select
                  size="sm"
                  placeholder="Programa"
                  value={newOverride.program_type}
                  onChange={(e) => setNewOverride((p) => ({ ...p, program_type: e.target.value }))}
                >
                  {programTypes.map((pt) => (
                    <option key={pt.id} value={pt.id}>
                      {(pt.attributes || pt).name}
                    </option>
                  ))}
                </Select>
              </Td>
              <Td isNumeric>
                <Input
                  type="number"
                  size="sm"
                  textAlign="right"
                  maxW="160px"
                  ml="auto"
                  placeholder="0"
                  value={newOverride.price}
                  onChange={(e) => setNewOverride((p) => ({ ...p, price: e.target.value }))}
                />
              </Td>
              <Td>
                <IconButton
                  aria-label="Agregar"
                  icon={<FiPlus />}
                  size="sm"
                  colorScheme="purple"
                  isLoading={addingOverride}
                  onClick={addOverride}
                />
              </Td>
            </Tr>
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
}
